import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, MessageSquareText, Instagram, Music, Facebook } from "lucide-react";
import { SMS_HREF, BRAND, LOGO_URL, INSTAGRAM_URL, TIKTOK_URL, FACEBOOK_URL } from "@/lib/site";
import { useSiteText } from "@/lib/siteConfig";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services & Pricing" },
    { to: "/gallery", label: "Work" },
    { to: "/articles", label: "Articles" },
    { to: "/refer", label: "Refer & Earn" },
    { to: "/feedback", label: "Feedback" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const insta = useSiteText("business.instagram_url", INSTAGRAM_URL);
    const tiktok = useSiteText("business.tiktok_url", TIKTOK_URL);
    const fb = useSiteText("business.facebook_url", FACEBOOK_URL);

    return (
        <header
            className="sticky top-0 z-40 border-b border-white/10 bg-black/85 backdrop-blur-xl"
            data-testid="site-header"
        >
            <div className="lbp-container flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    className="flex items-center gap-2.5 group"
                    data-testid="brand-logo-link"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src={LOGO_URL}
                        alt={BRAND}
                        className="h-10 w-10 md:h-11 md:w-11 object-contain"
                    />
                    <span className="font-display font-black uppercase tracking-tight text-white text-base sm:text-lg leading-none">
                        {BRAND}
                    </span>
                </Link>

                <nav
                    className="hidden xl:flex items-center gap-5"
                    data-testid="desktop-nav"
                >
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm font-semibold uppercase tracking-wider transition-colors ${
                                    isActive
                                        ? "text-white"
                                        : "text-zinc-400 hover:text-white"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <div className="hidden md:inline-flex items-center gap-1 mr-1" data-testid="nav-socials">
                        <a
                            href={insta}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-instagram-link"
                            aria-label="Instagram"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-white/5 transition-colors"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <a
                            href={tiktok}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-tiktok-link"
                            aria-label="TikTok"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-white/5 transition-colors"
                        >
                            <Music className="w-4 h-4" />
                        </a>
                        <a
                            href={fb}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-facebook-link"
                            aria-label="Facebook"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-white/5 transition-colors"
                        >
                            <Facebook className="w-4 h-4" />
                        </a>
                    </div>
                    <a
                        href={SMS_HREF}
                        data-testid="nav-text-now-button"
                        className="hidden sm:inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 active:scale-[0.98] transition-all text-white px-4 py-2.5 rounded-sm font-display font-bold uppercase tracking-wide text-xs sm:text-sm"
                    >
                        <MessageSquareText className="w-4 h-4" />
                        Text Now
                    </a>
                    <button
                        type="button"
                        className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm text-white hover:bg-white/10"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="xl:hidden border-t border-white/10 bg-black"
                    data-testid="mobile-nav-panel"
                >
                    <nav className="lbp-container py-4 flex flex-col">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                className={({ isActive }) =>
                                    `py-3 text-base font-display font-bold uppercase tracking-wider border-b border-white/5 ${
                                        isActive
                                            ? "text-red-500"
                                            : "text-white hover:text-red-400"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <a
                            href={SMS_HREF}
                            data-testid="mobile-nav-text-now"
                            onClick={() => setOpen(false)}
                            className="mt-4 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3.5 rounded-sm font-display font-bold uppercase tracking-wide"
                        >
                            <MessageSquareText className="w-4 h-4" />
                            Text Now for a Quote
                        </a>
                        <div className="mt-5 flex items-center justify-center gap-3 pt-4 border-t border-white/5" data-testid="mobile-nav-socials">
                            <a
                                href={insta}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Instagram"
                                data-testid="mobile-nav-instagram"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-sm border border-white/10 text-zinc-300 hover:text-red-500 hover:border-red-500/40 transition-colors"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                            <a
                                href={tiktok}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="TikTok"
                                data-testid="mobile-nav-tiktok"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-sm border border-white/10 text-zinc-300 hover:text-red-500 hover:border-red-500/40 transition-colors"
                            >
                                <Music className="w-5 h-5" />
                            </a>
                            <a
                                href={fb}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Facebook"
                                data-testid="mobile-nav-facebook"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-sm border border-white/10 text-zinc-300 hover:text-red-500 hover:border-red-500/40 transition-colors"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
